/* =============================================================================
   YSW FACE ART — the product artwork for the calendar, in two renderings:

     YSWFaceArt.drawFace(ctx, faceIndex, w, h)   -> canvas 2D, for the curl shader
     YSWFaceArt.markup(faceIndex)                -> HTML string, for the spun slab

   Both are the SAME artwork as CalLeaf.dc.html, at the same proportions: every
   number below is in `cqw` (1cqw = 1% of the page WIDTH), exactly as the DOM
   artwork is authored, and the canvas twin's positions were measured off the
   live DOM so the two renderings coincide.

   Colours, sizes, spacing and copy are the product's — unchanged.
   ========================================================================== */
(function (global) {
'use strict';

var SANS  = '"Poppins", system-ui, sans-serif';
var SERIF = '"Newsreader", Georgia, serif';

var INK = '#2B2926', MUTE = '#6f6a61', FAINT = '#9a948a';
var RULE = 'rgba(43,41,38,.30)', GRID = 'rgba(43,41,38,.16)', DOT = 'rgba(43,41,38,.5)';

var MONTH_WORDS = ['ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT','NINE','TEN','ELEVEN','TWELVE'];
var QUOTES = [
  'start small. then smaller than that.',
  'show up before you feel ready.',
  'the streak is the reward.',
  'missed a day? begin again, gently.',
  'consistency is a quiet kind of ambition.',
  'halfway is still moving.',
  "no new plan needed. just today's dot.",
  'slow progress is still progress.',
  'small things, repeated, become big things.',
  'the habit is keeping you now.',
  'nearly there is its own habit.',
  'look how far back the dots go.'
];
var GRID_NOTE = 'not every month gets a 31st';
var LAST_LINE = 'WHEN THE DOTS RUN OUT\u00a0\u00a0\u2014\u00a0\u00a0YEAROFSMALLWINS.COM';
var COVER_SUB = 'a calendar for keeping promises to yourself';
var COVER_FOOT = 'UNDATED\u00a0\u00a0\u00b7\u00a0\u00a012 MONTHS';

var theme = { bg: '#F2DEDA' };

/* ---------- measured layout (cqw) ---------- */
var L = {
  bindH: 6.2, bindShadowH: 1.8,
  padL: 6, padR: 94, contentW: 88,
  title:   { y: 10,     h: 3.325, size: 2.2,  ls: 0.15 },
  focusLb: { x: 64.08,  y: 11.137, h: 1.75,  size: 1.15, ls: 0.30 },
  focusLn: { x: 71.027, y: 10.769, w: 22.973, h: 2.4 },
  dow:     { y: 14.924, x: 6.599, w: 5.028, pitch: 88 / 7, t: 0.14 },
  grid:    { x: 6, y: 17.254, w: 88, h: 56.087, cols: 7, rows: 5, t: 0.11,
             daySize: 1.6, padT: 1, padL: 1.2, noteY: 66.062, noteH: 1.487, noteCx: 58.835, noteSize: 1.5 },
  habits:  { y: 78.342, labelH: 1.75, labelSize: 1.15, labelLs: 0.22,
             colX: 26.24, colW: 2.1858, markY: 78.451, markH: 1.531, markSize: 1.05, markLs: 0.22,
             rowY: 78.342 + 3.649, rowPitch: 4.699, rowH: 2.4, rows: 6,
             lineW: 17.811, dotD: 1.75, dotT: 0.13 },
  nw:      { y: 109.886, colW: 42.5, col2X: 51.5, labelH: 1.75, labelSize: 1.15, labelLs: 0.30,
             lineY: 113.636, linePitch: 5.349, lineH: 2.4, lines: 4 },
  quote:   { padT: 2, size: 2, lastSize: 1.1, lastLs: 0.26, lastGap: 1.6, bottomPad: 3.5 },
  cover:   { pad: 6, dotsTop: 49, dotD: 1.4, dotGap: 2.6,
             titleGap: 11, titleSize: 3.05, titleLh: 1.55, titleLs: 0.15,
             subGap: 5, subSize: 1.35, subLs: 0.01,
             footBottom: 11, ruleW: 13.3, ruleT: 0.14, ruleGap: 2.6, footSize: 1, footLs: 0.42 }
};

function faceKind(i) { return i === 0 ? 'cover' : (i === 13 ? 'blank' : 'month'); }
function faceTitle(i) { return 'MONTH ' + MONTH_WORDS[i - 1]; }
function faceQuote(i) { return QUOTES[i - 1]; }

/* =============================================================================
   CANVAS RENDERING
   ========================================================================== */
function drawFace(ctx, face, w, h) {
  var U = w / 100;                 // px per cqw
  var HH = h / U;                  // page height in cqw
  var kind = faceKind(face);

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = (kind === 'blank') ? '#ffffff' : theme.bg;
  ctx.fillRect(0, 0, w, h);
  if (kind === 'blank') { ctx.restore(); return; }

  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  var T = textTools(ctx, U);
  if (kind === 'cover') drawCover(ctx, U, HH, T);
  else drawMonth(ctx, U, HH, T, face);
  drawBinding(ctx, U);
  ctx.restore();
}

function textTools(ctx, U) {
  function font(size, o) {
    o = o || {};
    ctx.font = (o.italic ? 'italic ' : '') + (o.weight || 400) + ' ' +
               (size * U).toFixed(3) + 'px ' + (o.serif ? SERIF : SANS);
    try { ctx.letterSpacing = ((o.ls || 0) * size * U).toFixed(3) + 'px'; } catch (_) {}
  }
  function lineH() { var m = ctx.measureText('Hxg'); return (m.fontBoundingBoxAscent + m.fontBoundingBoxDescent) / U; }
  /* place a run of text inside a CSS line box: same half-leading rule the DOM uses */
  function text(str, x, boxTop, boxH, align) {
    var m = ctx.measureText(str);
    var asc = m.fontBoundingBoxAscent, desc = m.fontBoundingBoxDescent;
    var base = boxTop * U + (boxH * U - (asc + desc)) / 2 + asc;
    var px = x * U;
    if (align === 'center') px -= m.width / 2;
    else if (align === 'right') px -= m.width;
    ctx.fillText(str, px, base);
  }
  function rect(x, y, w, h, fill) { ctx.fillStyle = fill; ctx.fillRect(x * U, y * U, w * U, h * U); }
  return { font: font, lineH: lineH, text: text, rect: rect, U: U };
}

function drawMonth(ctx, U, HH, T, face) {
  var i, j;

  /* header */
  T.font(L.title.size, { ls: L.title.ls });
  ctx.fillStyle = INK;
  T.text(faceTitle(face), L.padL, L.title.y, L.title.h, 'left');

  T.font(L.focusLb.size, { ls: L.focusLb.ls });
  ctx.fillStyle = FAINT;
  T.text('FOCUS', L.focusLb.x, L.focusLb.y, L.focusLb.h, 'left');
  T.rect(L.focusLn.x, L.focusLn.y + L.focusLn.h - 0.14, L.focusLn.w, 0.14, RULE);

  /* day-of-week write-in dashes */
  for (i = 0; i < 7; i++) {
    T.rect(L.dow.x + i * L.dow.pitch, L.dow.y, L.dow.w, L.dow.t, 'rgba(43,41,38,.32)');
  }

  /* calendar grid */
  var g = L.grid, cw = g.w / g.cols, ch = g.h / g.rows;
  ctx.fillStyle = GRID;
  for (i = 0; i <= g.cols; i++) ctx.fillRect((g.x + i * cw) * U, g.y * U, g.t * U, g.h * U);
  for (j = 0; j <= g.rows; j++) ctx.fillRect(g.x * U, (g.y + j * ch) * U, g.w * U, g.t * U);

  T.font(g.daySize);
  for (i = 0; i < 31; i++) {
    var cx = g.x + (i % 7) * cw, cy = g.y + Math.floor(i / 7) * ch;
    ctx.fillStyle = MUTE;
    ctx.globalAlpha = i >= 28 ? 0.42 : 1;
    T.text(String(i + 1), cx + g.padL, cy + g.padT, g.daySize, 'left');
    ctx.globalAlpha = 1;
  }
  T.font(g.noteSize, { serif: true, italic: true });
  ctx.fillStyle = FAINT;
  T.text(GRID_NOTE, g.noteCx, g.noteY, g.noteH, 'center');

  /* habits */
  var hb = L.habits;
  T.font(hb.labelSize, { ls: hb.labelLs });
  ctx.fillStyle = FAINT;
  T.text('HABITS', L.padL, hb.y, hb.labelH, 'left');

  T.font(hb.markSize, { ls: hb.markLs });
  [1, 5, 10, 15, 20, 25, 31].forEach(function (n) {
    T.text(String(n), hb.colX + (n - 1) * hb.colW + hb.colW / 2, hb.markY, hb.markH, 'center');
  });

  for (i = 0; i < hb.rows; i++) {
    var ry = hb.rowY + i * hb.rowPitch;
    T.rect(L.padL, ry + hb.rowH - 0.14, hb.lineW, 0.14, RULE);
    for (j = 0; j < 31; j++) {
      var dx = hb.colX + j * hb.colW + hb.colW / 2;
      var dy = ry + hb.rowH / 2;
      ctx.beginPath();
      ctx.arc(dx * U, dy * U, (hb.dotD / 2 - hb.dotT / 2) * U, 0, Math.PI * 2);
      ctx.strokeStyle = DOT; ctx.lineWidth = hb.dotT * U; ctx.stroke();
    }
  }

  /* notes / wins */
  var nw = L.nw;
  T.font(nw.labelSize, { ls: nw.labelLs });
  ctx.fillStyle = FAINT;
  T.text('NOTES', L.padL, nw.y, nw.labelH, 'left');
  T.text('WINS', nw.col2X + nw.colW / 2, nw.y, nw.labelH, 'center');
  for (i = 0; i < nw.lines; i++) {
    var ly = nw.lineY + i * nw.linePitch + nw.lineH - 0.14;
    T.rect(L.padL, ly, nw.colW, 0.14, RULE);
    T.rect(nw.col2X, ly, nw.colW, 0.14, RULE);
  }

  /* quote — bottom-anchored (margin-top:auto) */
  var q = L.quote, bottom = HH - q.bottomPad;
  T.font(q.size, { serif: true, italic: true });
  var qh = T.lineH();
  var lastH = 0, lastLh = 0;
  if (face === 12) {
    T.font(q.lastSize, { ls: q.lastLs });
    lastLh = T.lineH();
    lastH = q.lastGap + lastLh;
  }
  var qTop = bottom - lastH - qh;
  T.font(q.size, { serif: true, italic: true });
  ctx.fillStyle = '#5a544b';
  T.text(faceQuote(face), 50, qTop, qh, 'center');
  if (face === 12) {
    T.font(q.lastSize, { ls: q.lastLs });
    ctx.fillStyle = FAINT;
    T.text(LAST_LINE, 50, qTop + qh + q.lastGap, lastLh, 'center');
  }
}

function drawCover(ctx, U, HH, T) {
  var c = L.cover, i;

  /* progress dots */
  var rowW = 7 * c.dotD + 6 * c.dotGap;
  var x0 = 50 - rowW / 2;
  var fills = ['rgba(43,41,38,.52)', 'rgba(43,41,38,.62)', 'rgba(43,41,38,.7)',
               'rgba(43,41,38,.78)', 'rgba(43,41,38,.86)', INK, null];
  for (i = 0; i < 7; i++) {
    var cx = (x0 + i * (c.dotD + c.dotGap) + c.dotD / 2) * U;
    var cy = (c.dotsTop + c.dotD / 2) * U;
    ctx.beginPath();
    if (fills[i]) {
      ctx.arc(cx, cy, (c.dotD / 2) * U, 0, Math.PI * 2);
      ctx.fillStyle = fills[i]; ctx.fill();
    } else {
      ctx.arc(cx, cy, (c.dotD / 2 - 0.09) * U, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(43,41,38,.6)'; ctx.lineWidth = 0.18 * U; ctx.stroke();
    }
  }

  /* title */
  var tTop = c.dotsTop + c.dotD + c.titleGap;
  var tLh = c.titleSize * c.titleLh;
  T.font(c.titleSize, { weight: 300, ls: c.titleLs });
  ctx.fillStyle = '#3a382f';
  T.text('THE YEAR OF', 50, tTop, tLh, 'center');
  T.text('SMALL WINS', 50, tTop + tLh, tLh, 'center');

  /* subtitle */
  T.font(c.subSize, { serif: true, italic: true, ls: c.subLs });
  var sLh = T.lineH();
  ctx.fillStyle = '#6f6a61';
  T.text(COVER_SUB, 50, tTop + 2 * tLh + c.subGap, sLh, 'center');

  /* footer */
  T.font(c.footSize, { ls: c.footLs });
  var fLh = T.lineH();
  var blockH = c.ruleT + c.ruleGap + fLh;
  var fTop = HH - c.footBottom - blockH;
  T.rect(50 - c.ruleW / 2, fTop, c.ruleW, c.ruleT, 'rgba(43,41,38,.3)');
  ctx.fillStyle = '#8a857c';
  T.text(COVER_FOOT, 50, fTop + c.ruleT + c.ruleGap, fLh, 'center');
}

/* ---------- wire-o binding (the CalLeaf SVG, in canvas units) ----------
   The SVG is viewBox="0 0 1000 62" laid over 100cqw x 6.2cqw, so one SVG unit
   is 0.1cqw in both axes: scale into SVG space and the geometry is verbatim. */
function drawBinding(ctx, U) {
  var s = 0.1 * U;
  ctx.save();
  ctx.scale(s, s);

  ctx.fillStyle = '#fdfdfb'; ctx.fillRect(0, 24, 1000, 14);
  ctx.fillStyle = 'rgba(43,41,38,.13)'; ctx.fillRect(0, 24, 1000, 0.9);
  var railSh = ctx.createLinearGradient(0, 38, 0, 42);
  railSh.addColorStop(0, 'rgba(43,41,38,.16)');
  railSh.addColorStop(1, 'rgba(43,41,38,0)');
  ctx.fillStyle = railSh; ctx.fillRect(0, 38, 1000, 4);

  function ell(cx, cy, rx, ry) { ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); }
  ell(500, 30, 23, 27);
  ctx.fillStyle = '#fdfdfb'; ctx.fill();
  ctx.strokeStyle = 'rgba(43,41,38,.13)'; ctx.lineWidth = 1; ctx.stroke();
  ell(500, 31, 11, 15); ctx.fillStyle = '#c9c5bd'; ctx.fill();
  ell(500, 29.4, 11, 15); ctx.fillStyle = 'rgba(43,41,38,.14)'; ctx.fill();

  var wire = ctx.createLinearGradient(0, 6, 0, 46);
  wire.addColorStop(0, '#fbfbfc');
  wire.addColorStop(0.28, '#dedee3');
  wire.addColorStop(0.62, '#a7a7ae');
  wire.addColorStop(0.86, '#c6c6cc');
  wire.addColorStop(1, '#eeeef1');

  [[0, 461], [539, 1000]].forEach(function (span) {
    ctx.save();
    ctx.beginPath(); ctx.rect(span[0], 0, span[1] - span[0], 62); ctx.clip();
    for (var tx = Math.floor(span[0] / 28.6) * 28.6; tx < span[1]; tx += 28.6) {
      ctx.save();
      ctx.translate(tx, 0);
      ctx.fillStyle = 'rgba(43,41,38,.14)';
      ell(3, 46, 3.6, 1.7); ctx.fill();
      ell(25, 46, 3.6, 1.7); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(2.5, 46);
      ctx.bezierCurveTo(3.5, 24, 7.5, 7, 14, 6.5);
      ctx.bezierCurveTo(20.5, 6, 23.5, 24, 24.5, 46);
      ctx.strokeStyle = wire; ctx.lineWidth = 6.6; ctx.lineCap = 'round'; ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(4.4, 40);
      ctx.bezierCurveTo(5.2, 24, 8.4, 11, 13.4, 10.4);
      ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  });
  ctx.restore();

  /* the soft shadow the paper casts just under the rail */
  var sh = ctx.createLinearGradient(0, L.bindH * U, 0, (L.bindH + L.bindShadowH) * U);
  sh.addColorStop(0, 'rgba(43,41,38,.09)');
  sh.addColorStop(1, 'rgba(43,41,38,0)');
  ctx.fillStyle = sh;
  ctx.fillRect(0, L.bindH * U, 100 * U, L.bindShadowH * U);
}

/* =============================================================================
   DOM RENDERING (the spun slab) — the same artwork as markup
   ========================================================================== */
function bindingMarkup(id) {
  return '' +
  '<div style="position:absolute; top:0; left:0; width:100%; height:6.2cqw; z-index:6;">' +
    '<svg viewBox="0 0 1000 62" style="display:block; width:100%; height:100%;">' +
      '<defs>' +
        '<linearGradient id="ywWire' + id + '" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0" stop-color="#fbfbfc"/><stop offset="0.28" stop-color="#dedee3"/>' +
          '<stop offset="0.62" stop-color="#a7a7ae"/><stop offset="0.86" stop-color="#c6c6cc"/>' +
          '<stop offset="1" stop-color="#eeeef1"/>' +
        '</linearGradient>' +
        '<linearGradient id="ywRailSh' + id + '" x1="0" y1="0" x2="0" y2="1">' +
          '<stop offset="0" stop-color="rgba(43,41,38,.16)"/><stop offset="1" stop-color="rgba(43,41,38,0)"/>' +
        '</linearGradient>' +
        '<pattern id="ywCoil' + id + '" width="28.6" height="62" patternUnits="userSpaceOnUse">' +
          '<ellipse cx="3" cy="46" rx="3.6" ry="1.7" fill="rgba(43,41,38,.14)"/>' +
          '<ellipse cx="25" cy="46" rx="3.6" ry="1.7" fill="rgba(43,41,38,.14)"/>' +
          '<path d="M2.5 46 C3.5 24 7.5 7 14 6.5 C20.5 6 23.5 24 24.5 46" fill="none" stroke="url(#ywWire' + id + ')" stroke-width="6.6" stroke-linecap="round"/>' +
          '<path d="M4.4 40 C5.2 24 8.4 11 13.4 10.4" fill="none" stroke="rgba(255,255,255,.55)" stroke-width="1.5" stroke-linecap="round"/>' +
        '</pattern>' +
      '</defs>' +
      '<rect x="0" y="24" width="1000" height="14" fill="#fdfdfb"/>' +
      '<rect x="0" y="24" width="1000" height="0.9" fill="rgba(43,41,38,.13)"/>' +
      '<rect x="0" y="38" width="1000" height="4" fill="url(#ywRailSh' + id + ')"/>' +
      '<ellipse cx="500" cy="30" rx="23" ry="27" fill="#fdfdfb" stroke="rgba(43,41,38,.13)" stroke-width="1"/>' +
      '<ellipse cx="500" cy="31" rx="11" ry="15" fill="#c9c5bd"/>' +
      '<ellipse cx="500" cy="29.4" rx="11" ry="15" fill="rgba(43,41,38,.14)"/>' +
      '<rect x="0" y="0" width="461" height="62" fill="url(#ywCoil' + id + ')"/>' +
      '<rect x="539" y="0" width="461" height="62" fill="url(#ywCoil' + id + ')"/>' +
    '</svg>' +
  '</div>' +
  '<div style="position:absolute; top:6.2cqw; left:0; right:0; height:1.8cqw; z-index:5; background:linear-gradient(rgba(43,41,38,.09), rgba(43,41,38,0));"></div>';
}

function coverMarkup() {
  var dots = ['rgba(43,41,38,.52)', 'rgba(43,41,38,.62)', 'rgba(43,41,38,.7)',
              'rgba(43,41,38,.78)', 'rgba(43,41,38,.86)', '#2B2926'].map(function (c) {
    return '<span style="width:1.4cqw;height:1.4cqw;border-radius:50%;background:' + c + ';"></span>';
  }).join('') +
  '<span style="width:1.4cqw;height:1.4cqw;border-radius:50%;border:0.18cqw solid rgba(43,41,38,.6);box-sizing:border-box;"></span>';

  return '<div style="position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; padding:6cqw;">' +
    '<div aria-hidden="true" style="display:flex; gap:2.6cqw; align-items:center; justify-content:center; margin-top:43cqw;">' + dots + '</div>' +
    '<div style="text-align:center; font-size:3.05cqw; font-weight:300; letter-spacing:.15em; line-height:1.55; color:#3a382f; margin-top:11cqw;">THE YEAR OF<br>SMALL WINS</div>' +
    '<div class="cl-sf" style="font-style:italic; font-size:1.35cqw; color:#6f6a61; margin-top:5cqw; text-align:center; letter-spacing:.01em;">' + COVER_SUB + '</div>' +
    '<div style="position:absolute; bottom:11cqw; left:50%; transform:translateX(-50%); text-align:center;">' +
      '<div style="width:13.3cqw; height:0.14cqw; background:rgba(43,41,38,.3); margin:0 auto 2.6cqw;"></div>' +
      '<div style="font-size:1cqw; letter-spacing:.42em; color:#8a857c;">' + COVER_FOOT + '</div>' +
    '</div>' +
  '</div>';
}

function monthMarkup(face) {
  var i, s = '';

  s += '<div style="position:absolute; inset:0; padding:10cqw 6cqw 3.5cqw; display:flex; flex-direction:column;">';

  s += '<div style="display:flex; justify-content:space-between; align-items:baseline; gap:5cqw;">' +
         '<div style="font-size:2.2cqw; font-weight:400; letter-spacing:.15em; color:#2B2926; flex:none;">' + faceTitle(face) + '</div>' +
         '<div style="display:flex; align-items:baseline; gap:1.4cqw; flex:1; max-width:34%;">' +
           '<span style="font-size:1.15cqw; letter-spacing:.3em; color:#9a948a; flex:none;">FOCUS</span>' +
           '<input class="cl-line" style="flex:1; font-size:1.5cqw;">' +
         '</div>' +
       '</div>';

  s += '<div style="display:grid; grid-template-columns:repeat(7,1fr); margin-top:1.6cqw; gap:0;">';
  for (i = 0; i < 7; i++) {
    s += '<div style="display:flex; justify-content:flex-start; padding-left:0.6cqw;"><span style="width:42%; border-bottom:0.14cqw solid rgba(43,41,38,.32);"></span></div>';
  }
  s += '</div>';

  s += '<div style="position:relative; display:grid; grid-template-columns:repeat(7,1fr); grid-template-rows:repeat(5,1fr); height:56cqw; flex:none; margin-top:2.2cqw; border-left:0.11cqw solid rgba(43,41,38,.16); border-top:0.11cqw solid rgba(43,41,38,.16);">';
  for (i = 0; i < 35; i++) {
    var n = i < 31 ? (i + 1) : '';
    var op = (i + 1 > 28 && i < 31) ? '.42' : '1';
    s += '<div style="border-right:0.11cqw solid rgba(43,41,38,.16); border-bottom:0.11cqw solid rgba(43,41,38,.16); font-size:1.6cqw; color:#6f6a61; padding:1cqw 0 0 1.2cqw; line-height:1; opacity:' + op + ';">' + n + '</div>';
  }
  s += '<div class="cl-sf" style="position:absolute; top:87%; left:20%; right:0; text-align:center; font-style:italic; font-size:1.5cqw; color:#9a948a;">' + GRID_NOTE + '</div>';
  s += '</div>';

  s += '<div style="margin-top:5cqw;">' +
         '<div style="display:grid; grid-template-columns:23% 1fr; align-items:center; color:#9a948a; font-size:1.15cqw; letter-spacing:.22em;">' +
           '<div style="letter-spacing:.22em;">HABITS</div>' +
           '<div style="display:grid; grid-template-columns:repeat(31,1fr); text-align:center;">';
  [1, 5, 10, 15, 20, 25, 31].forEach(function (n) {
    s += '<span style="grid-column:' + n + '; font-size:1.05cqw;">' + n + '</span>';
  });
  s += '</div></div><div style="display:flex; flex-direction:column; gap:2.3cqw; margin-top:1.9cqw;">';
  for (i = 0; i < 6; i++) {
    s += '<div style="display:grid; grid-template-columns:23% 1fr; align-items:center;">' +
           '<input class="cl-line" style="width:88%; font-size:1.4cqw;">' +
           '<div style="display:grid; grid-template-columns:repeat(31,1fr); align-items:center; justify-items:center;">';
    for (var j = 0; j < 31; j++) {
      s += '<button type="button" class="cl-dot" data-k="' + face + '-' + i + '-' + j + '" aria-label="fill dot" style="width:1.75cqw; height:1.75cqw;"></button>';
    }
    s += '</div></div>';
  }
  s += '</div></div>';

  function lines(n) {
    var out = '';
    for (var k = 0; k < n; k++) {
      out += '<input class="cl-line" style="width:100%; ' + (k < n - 1 ? 'margin-bottom:2.95cqw; ' : '') + 'font-size:1.4cqw;">';
    }
    return out;
  }
  s += '<div style="display:grid; grid-template-columns:1fr 1fr; gap:3cqw; margin-top:2cqw;">' +
         '<div><div style="font-size:1.15cqw; letter-spacing:.3em; color:#9a948a; margin-bottom:2cqw;">NOTES</div>' + lines(4) + '</div>' +
         '<div><div style="font-size:1.15cqw; letter-spacing:.3em; color:#9a948a; margin-bottom:2cqw; text-align:center;">WINS</div>' + lines(4) + '</div>' +
       '</div>';

  s += '<div style="text-align:center; margin-top:auto; padding-top:2cqw;">' +
         '<div class="cl-sf" style="font-style:italic; font-size:2cqw; color:#5a544b;">' + faceQuote(face) + '</div>' +
         (face === 12 ? '<div style="font-size:1.1cqw; letter-spacing:.26em; color:#9a948a; margin-top:1.6cqw;">' + LAST_LINE + '</div>' : '') +
       '</div>';

  s += '</div>';
  return s;
}

function markup(face) {
  var kind = faceKind(face);
  var bg = kind === 'blank' ? '#ffffff' : theme.bg;
  var body = kind === 'blank' ? '' : (bindingMarkup(face) + (kind === 'cover' ? coverMarkup() : monthMarkup(face)));
  return '<div class="cl-face" style="position:absolute; inset:0; overflow:hidden; background:' + bg +
         '; font-family:' + SANS.replace(/"/g, "'") + '; color:#2B2926;">' + body + '</div>';
}

global.YSWFaceArt = {
  drawFace: drawFace,
  markup: markup,
  setBg: function (hex) { theme.bg = hex; },
  faceLabel: function (i) {
    if (i === 0) return 'THE YEAR OF SMALL WINS';
    if (i === 13) return 'BACK COVER';
    return 'MONTH ' + MONTH_WORDS[i - 1] + '\u2002\u00b7\u2002' + String(i).padStart(2, '0') + ' / 12';
  }
};
}(typeof self !== 'undefined' ? self : this));
